/** Returns a short relative-time string like "just now", "5m ago", "3h ago",
 * "2d ago", "3 weeks ago", "6 months ago". */
export function relativeTime(iso: string | number | Date): string {
  const then = typeof iso === "string" || typeof iso === "number"
    ? new Date(iso).getTime()
    : iso.getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));

  if (diffSec < 10) return "just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return "yesterday";
  if (diffDay < 7) return `${diffDay}d ago`;
  if (diffDay < 30) return `${Math.floor(diffDay / 7)}w ago`;
  if (diffDay < 365) return `${Math.floor(diffDay / 30)}mo ago`;
  return `${Math.floor(diffDay / 365)}y ago`;
}

/** Formats a bytes count into "X.Y MB" or "X KB". */
export function formatBytes(bytes: number | undefined | null): string {
  if (!bytes || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/** Placeholder regex shared between teleprompter + new-link preview. */
export const PLACEHOLDER_REGEX = /(\.\.\.\([^)]*\)\.\.\.)/g;
export const PLACEHOLDER_INNER = /^\.\.\.\(([^)]*)\)\.\.\.$/;

// Combined tokenizer: ...(lemonade)..., [orange]. Used to split a line into
// renderable parts while preserving order.
export const SCRIPT_TOKEN_REGEX = /(\.\.\.\([^)]*\)\.\.\.|\[[^\]]*\])/g;
export const BRACKET_INNER = /^\[([^\]]*)\]$/;

// Segment marker: a line starting with "(N)", "(N.M)", or "()" (with any
// surrounding whitespace). An optional "*" or "**" right before the
// parenthesis opts the segment into karaoke. The first capture group is the
// star sequence ("", "*", or "**"); the second is the numeric content —
// empty string means "auto-distribute".
const SEGMENT_MARKER = /^\s*(\*{0,2})\s*\((\d*(?:\.\d+)?)\)\s*/;

/** Seconds of silent buffer inserted between consecutive blocks. The
 *  recorder never stops, so this gives a clean cut point for the transcoder
 *  to split the raw mp4 into N separate videos at the block boundaries. */
export const BLOCK_BUFFER_SEC = 1;

export interface Segment {
  /** Segment text (may contain newlines — line breaks within a segment). */
  raw: string;
  /** Explicit duration in seconds from "(N)" marker, undefined when "()" or unset. */
  duration?: number;
  /** Karaoke rendering for the segment:
   *  - "phrase" (`*` prefix): per-phrase underline fill with a small breath
   *    between phrase groups (split on punctuation and newlines).
   *  - "instant" (`**` prefix): full underline appears under every word at
   *    once the segment becomes active. */
  karaoke?: "phrase" | "instant";
  /** Synthetic gap inserted between two blocks. Renders nothing on the
   *  teleprompter and is not shown in the recorder's segment counter. */
  kind?: "buffer";
  /** Index of the block this segment belongs to (0-based). Buffers carry
   *  `blockIndex: -1` since they sit between two blocks. */
  blockIndex?: number;
}

export interface ParsedScript {
  /** Lines shown before recording starts (before the FIRST "---"). */
  intro: string[];
  /** Recording-time segments delimited by "(N)" / "()" markers. When the
   *  body contains additional "---" separators, the segments span multiple
   *  blocks and `BLOCK_BUFFER_SEC`-long buffer segments are inserted at the
   *  boundaries (kind === "buffer"). */
  segments: Segment[];
  /** Number of blocks parsed from the body (1 when no extra "---"). */
  blockCount: number;
}

/** Parse a raw teleprompter script into intro + segments.
 *  - The FIRST "---" on its own line separates intro from recording body.
 *  - In the body, additional "---" lines split the body into BLOCKS. Each
 *    block becomes its own set of segments; a synthetic buffer segment of
 *    `BLOCK_BUFFER_SEC` seconds is inserted between consecutive blocks so
 *    the post-prod transcoder can cut the raw mp4 cleanly into N parts.
 *  - Inside a block, "(N)" or "()" at the start of a line opens a new
 *    segment; lines without a marker append to the current segment.
 *  - Legacy fallback: if a block has NO markers at all, each non-empty line
 *    becomes its own auto-duration segment. */
export function parseScript(text: string): ParsedScript {
  const lines = text.split(/\r?\n/);
  const sepIdx = lines.findIndex((l) => l.trim() === "---");
  const introRaw = sepIdx >= 0 ? lines.slice(0, sepIdx) : [];
  const bodyRaw = sepIdx >= 0 ? lines.slice(sepIdx + 1) : lines;

  const intro = introRaw.map((l) => l.trim()).filter(Boolean);

  // Split body into blocks at every additional "---" line.
  const rawBlocks: string[][] = [[]];
  for (const line of bodyRaw) {
    if (line.trim() === "---") {
      rawBlocks.push([]);
    } else {
      rawBlocks[rawBlocks.length - 1].push(line);
    }
  }
  // Drop blocks made of only blank lines so a dangling "---" or a
  // double-"---" never produces a phantom block (and thus no orphan buffer).
  const blocks = rawBlocks.filter((b) => b.some((l) => l.trim()));
  if (blocks.length === 0) blocks.push([]);

  const segments: Segment[] = [];
  blocks.forEach((blockLines, blockIdx) => {
    if (blockIdx > 0) {
      segments.push({
        raw: "",
        duration: BLOCK_BUFFER_SEC,
        kind: "buffer",
        blockIndex: -1,
      });
    }
    for (const seg of parseBlock(blockLines)) {
      seg.blockIndex = blockIdx;
      segments.push(seg);
    }
  });

  return { intro, segments, blockCount: blocks.length };
}

function parseBlock(bodyRaw: string[]): Segment[] {
  const hasAnyMarker = bodyRaw.some((l) => SEGMENT_MARKER.test(l));
  if (!hasAnyMarker) {
    return bodyRaw
      .map((l) => l.trim())
      .filter(Boolean)
      .map((line) => ({ raw: line }));
  }

  const segments: Segment[] = [];
  let current:
    | { lines: string[]; duration?: number; karaoke?: Segment["karaoke"] }
    | null = null;
  const flush = () => {
    if (!current) return;
    const raw = current.lines.join("\n").replace(/^\n+|\n+$/g, "");
    if (raw) {
      const seg: Segment = { raw, duration: current.duration };
      if (current.karaoke) seg.karaoke = current.karaoke;
      segments.push(seg);
    }
    current = null;
  };

  for (const line of bodyRaw) {
    const m = line.match(SEGMENT_MARKER);
    if (m) {
      flush();
      const rest = line.slice(m[0].length);
      const dur = m[2] === "" ? undefined : parseFloat(m[2]);
      const karaoke: Segment["karaoke"] =
        m[1] === "**" ? "instant" : m[1] === "*" ? "phrase" : undefined;
      current = { lines: rest ? [rest] : [], duration: dur, karaoke };
    } else if (current) {
      current.lines.push(line);
    } else if (line.trim()) {
      // Lines before the first marker → initial auto-duration segment.
      current = { lines: [line], duration: undefined };
    }
  }
  flush();

  return segments;
}

/** Strip markup and count whitespace-separated tokens. Used as a weight when
 *  distributing time across auto-duration segments. */
function wordCount(s: string): number {
  const cleaned = s
    .replace(/\.\.\.\(([^)]*)\)\.\.\./g, "$1")
    .replace(/\[([^\]]*)\]/g, "$1");
  return cleaned.split(/\s+/).filter(Boolean).length;
}

/** Compute per-segment timing in seconds.
 *  - Buffer segments (kind === "buffer") always keep their declared duration
 *    — they are reserved from maxSec before distribution so block-cut gaps
 *    stay exactly BLOCK_BUFFER_SEC long regardless of script length.
 *  - Segments with explicit "(N)" durations keep N (scaled down if their sum
 *    overflows the remaining budget).
 *  - Auto segments ("()" or no marker) share the remaining time proportionally
 *    to their word count, so a long sentence gets more screen time than a
 *    short one. */
export function distributeTiming(
  segments: Segment[],
  maxSec: number,
): { start: number; dur: number }[] {
  if (segments.length === 0) return [];

  const bufferSum = segments
    .filter((s) => s.kind === "buffer")
    .reduce((acc, s) => acc + (s.duration ?? 0), 0);
  const realBudget = Math.max(0, maxSec - bufferSum);

  const realSegs = segments.filter((s) => s.kind !== "buffer");
  const explicitSum = realSegs.reduce((s, x) => s + (x.duration ?? 0), 0);
  const autoSegs = realSegs.filter((x) => x.duration === undefined);
  const autoCount = autoSegs.length;

  let scale = 1;
  let autoBudget = 0;
  if (autoCount === 0) {
    if (explicitSum > 0) scale = realBudget / explicitSum;
  } else if (explicitSum >= realBudget) {
    // Explicit alone overflows: shrink them to 90% of the real budget,
    // give 10% to auto.
    scale = (realBudget * 0.9) / explicitSum;
    autoBudget = realBudget * 0.1;
  } else {
    autoBudget = realBudget - explicitSum;
  }

  const autoWordSum =
    autoSegs.reduce((acc, seg) => acc + Math.max(1, wordCount(seg.raw)), 0) || 1;

  let t = 0;
  return segments.map((seg) => {
    let dur: number;
    if (seg.kind === "buffer") {
      dur = seg.duration ?? BLOCK_BUFFER_SEC;
    } else if (seg.duration !== undefined) {
      dur = seg.duration * scale;
    } else {
      const w = Math.max(1, wordCount(seg.raw));
      dur = (w / autoWordSum) * autoBudget;
    }
    const out = { start: t, dur };
    t += dur;
    return out;
  });
}

/** Per-block recording timings (in seconds, relative to t=0 of the raw mp4).
 *  Buffer gaps are EXCLUDED — start_sec / end_sec bound the audible content
 *  of each block, so the transcoder cuts the source mp4 inside the buffer
 *  on both sides and never clips the destinataire's voice. */
export interface BlockTiming {
  start_sec: number;
  end_sec: number;
}

/** Compute block-cut timings from a script + maxSec. Returns one entry per
 *  block. Mono-block scripts return an array of length 1 covering the full
 *  recording (callers can choose to skip the split when length === 1).
 *
 *  Each block's end_sec is extended to the START of the next block (rather
 *  than the end of its own last segment). The destinataire's tail audio
 *  during the inter-block buffer stays attached to the BLOCK THEY JUST
 *  FINISHED — so we don't clip the end of their sentence. The cut between
 *  part N and part N+1 lands exactly where the next block's subtitles
 *  appear on the teleprompter. Last block keeps its real end. */
export function computeBlockTimings(
  text: string,
  maxSec: number,
): BlockTiming[] {
  const parsed = parseScript(text);
  const timing = distributeTiming(parsed.segments, maxSec);
  const byBlock = new Map<number, { start: number; end: number }>();
  parsed.segments.forEach((seg, i) => {
    if (seg.kind === "buffer") return;
    const blockIdx = seg.blockIndex ?? 0;
    const t = timing[i];
    if (!t) return;
    const cur = byBlock.get(blockIdx);
    const segEnd = t.start + t.dur;
    if (!cur) {
      byBlock.set(blockIdx, { start: t.start, end: segEnd });
    } else {
      cur.end = segEnd;
    }
  });
  const blocks = Array.from(byBlock.values()).sort((a, b) => a.start - b.start);
  for (let i = 0; i < blocks.length - 1; i++) {
    blocks[i].end = blocks[i + 1].start;
  }
  return blocks.map(({ start, end }) => ({ start_sec: start, end_sec: end }));
}

/** Find which segment index is active at a given elapsed-second mark. */
export function currentSegmentIdx(
  elapsedSec: number,
  timing: { start: number; dur: number }[],
): number {
  if (timing.length === 0) return -1;
  for (let i = 0; i < timing.length; i++) {
    if (elapsedSec >= timing[i].start && elapsedSec < timing[i].start + timing[i].dur) {
      return i;
    }
  }
  return timing.length - 1;
}

// Emoticon → emoji map, longest patterns first so ":'(" wins over ":(".
const EMOJI_PAIRS: [RegExp, string][] = [
  [/:'\(/g, "😢"],
  [/:D(?=\W|$)/g, "😀"],
  [/XD(?=\W|$)/g, "😆"],
  [/:[pP](?=\W|$)/g, "😛"],
  [/:[oO](?=\W|$)/g, "😮"],
  [/;\)/g, "😉"],
  [/:\)/g, "🙂"],
  [/:\(/g, "🙁"],
  [/<3/g, "❤️"],
];

/** Replace text emoticons with unicode emoji. Safe inside placeholders. */
export function emojify(text: string): string {
  return EMOJI_PAIRS.reduce((s, [re, em]) => s.replace(re, em), text);
}
